import { generateRoomPartId, getCurrentTimestamp, simpleClone } from './utils';
import { DeviceValidationError, UnsupportedOperationError } from './errors';
import {
  Device,
  UnfinishedItem,
  Hood,
  Group,
  Room,
  Zone,
  Exhaust,
  VAV,
  UnfinishedItemOfType,
  Supply,
  Appliance,
} from '@/types/helpers';

import { createMeasurement } from '@/common/measurements';

import partialHoodRaw from '@/fixtures/hood.fixture.json' with { type: 'json' };
import partialExhaustRaw from '@/fixtures/exhaust.fixture.json' with { type: 'json' };
import partialSupplyRaw from '@/fixtures/supply.fixture.json' with { type: 'json' };
import { RowBuilder } from './itemUtils';

const partialHood = partialHoodRaw as unknown as UnfinishedItemOfType<'hood'>;
const partialExhaust = partialExhaustRaw as unknown as UnfinishedItemOfType<'exhaust'>;
const partialSupply = partialSupplyRaw as unknown as UnfinishedItemOfType<'supply'>;

export function getEmptyDevice(type: 'hood'): UnfinishedItemOfType<'hood'>;
export function getEmptyDevice(type: 'exhaust'): UnfinishedItemOfType<'exhaust'>;
export function getEmptyDevice(type: 'supply'): UnfinishedItemOfType<'supply'>;
export function getEmptyDevice<T extends 'hood' | 'exhaust' | 'supply'>(type: T): UnfinishedItemOfType<T> {
  if (type !== 'hood' && type !== 'exhaust' && type !== 'supply') {
    throw new UnsupportedOperationError(`Can not create empty devices of type ${type}`);
  }
  if (type === 'hood') {
    return {
      ...partialHood,
      id: generateRoomPartId(),
    } as UnfinishedItemOfType<T>;
  }
  if (type === 'supply') {
    return {
      ...partialSupply,
      id: generateRoomPartId(),
    } as UnfinishedItemOfType<T>;
  }
  return {
    ...partialExhaust,
    id: generateRoomPartId(),
  } as UnfinishedItemOfType<T>;
}

export function validateUnfinishedHood(hood: UnfinishedItem<Hood> | Hood): Hood {
  if (!(hood as UnfinishedItem<Hood>).incomplete) {
    return hood as Hood;
  }

  const h = hood as UnfinishedItem<Hood>;

  const { incomplete, ...rest } = h;
  if (!rest.haltonValues) {
    throw new DeviceValidationError('Hood missing required values');
  }

  return rest as Hood;
}

export function validateUnfinishedExhaust(exhaust: UnfinishedItem<Exhaust> | Exhaust): Exhaust {
  if (!(exhaust as UnfinishedItem<Exhaust>).incomplete) {
    return exhaust as Exhaust;
  }

  const e = exhaust as UnfinishedItem<Exhaust>;

  const { incomplete, ...rest } = e;
  if (!rest.haltonValues) {
    throw new DeviceValidationError('Exhaust missing required values');
  }

  return rest as Exhaust;
}

export function validateUnfinishedSupply(supply: UnfinishedItem<Supply> | Supply): Supply {
  if (!(supply as UnfinishedItem<Supply>).incomplete) {
    return supply as Supply;
  }

  const s = supply as UnfinishedItem<Supply>;

  const { incomplete, ...rest } = s;
  if (!rest.haltonValues) {
    throw new DeviceValidationError('Supply missing required values');
  }

  return rest as Supply;
}

export function createZone(projectVersion: `v${string}`): Zone {
  const now = getCurrentTimestamp();
  return {
    id: generateRoomPartId(),
    schemaPath: `/schemas/${projectVersion}/zone/zone.schema.json`,
    type: 'zone',
    haltonValues: {
      type: 'zone',
      totalSupplyAirflow: {
        value: 0,
        type: 'volumetricFlowRate',
        unit: 'm^3/h',
      },
      totalExhaustAirflow: {
        value: 0,
        type: 'volumetricFlowRate',
        unit: 'm^3/h',
      },
    },
    relations: {
      supplyIds: [],
      exhaustIds: [],
      roomIds: [],
    },
    airchefValues: {
      name: '',
      comment: '',
      datesAndAudit: {
        createdAt: now,
        modifiedAt: now,
      },
    },
  };
}

export function createVav(name: string, projectVersion: `v${string}`, type: 'vav-na'): VAV {
  const now = getCurrentTimestamp();

  return {
    id: generateRoomPartId(),
    schemaPath: `/schemas/${projectVersion}/vav/vav.schema.json`,
    type: 'vav',
    airchefValues: {
      name,
      datesAndAudit: {
        createdAt: now,
        modifiedAt: now,
        modifiedBy: 'system',
        author: 'system',
      },
      transform: {
        position: {
          x: createMeasurement(0, 'mm'),
          y: createMeasurement(0, 'mm'),
          z: createMeasurement(0, 'mm'),
        },
        size: {
          width: createMeasurement(0, 'mm'),
          height: createMeasurement(0, 'mm'),
          length: createMeasurement(0, 'mm'),
        },
        rotation: {
          x: 0,
          y: 0,
          z: 0,
        },
      },
    },
    haltonValues: {
      type,
    },
    relations: {},
  };
}

export function createRoom(projectVersion: `v${string}`): Room {
  const now = getCurrentTimestamp();
  return {
    id: generateRoomPartId(),
    schemaPath: `/schemas/${projectVersion}/room/room.schema.json`,
    type: 'room',
    airchefValues: {
      name: '',
      datesAndAudit: {
        createdAt: now,
        modifiedAt: now,
      },
    },
    relations: {
      corners: {},
      walls: {},
      sectors: {},
      zoneIds: [],
    },
    haltonValues: {
      type: 'room',
      id: 1,
      heatload: {
        schemaPath: `/schemas/${projectVersion}/heatload/heatload.schema.json`,
        type: 'heatload',
      },
      hvac: {
        schemaPath: `/schemas/${projectVersion}/hvac/hvac.schema.json`,
        type: 'hvac',
      },
    },
  };
}

/**
 * Clones an appliance and returns the clone, along with any other updated assets.
 * Currently only supports cloning individual appliances, do we need to support cloning groups?
 * @param asset
 * @param assets
 * @param groups
 * @returns
 */
export function cloneAsset(
  asset: Device,
  _assets: Device[],
  groups: Group[],
  alwaysGroup: boolean,
  emptyGroup: Group,
): { updatedAssets: Device[]; newAssets: Device[]; updatedGroups: Group[] } {
  const updatedAssets: Device[] = [];
  const newAssets: Device[] = [];
  const updatedGroups: Group[] = [];

  if (asset.type !== 'appliance') {
    console.warn('Can only clone appliances!');
    return {
      updatedAssets,
      newAssets,
      updatedGroups,
    };
  }

  const now = getCurrentTimestamp();
  const clone = simpleClone(asset);
  clone.id = generateRoomPartId();
  clone.airchefValues.datesAndAudit = {
    createdAt: now,
    modifiedAt: now,
  };

  newAssets.push(clone);

  if (clone.relations.groupId) {
    // This appliance is in a group
    // Add the clone after the original to the group
    const updatedGroup = updateGroupWithClone(asset, clone, groups);
    if (updatedGroup) updatedGroups.push(updatedGroup);
  } else if (!clone.relations.groupId && alwaysGroup && clone.relations.roomId) {
    // Lone appliance cloned, and we want to group it with its clone, create a new group.
    const baseAsset = simpleClone(asset);
    const row = new RowBuilder().addAppliance(baseAsset.id).addAppliance(clone.id).build();
    const group = {
      ...emptyGroup,
      id: generateRoomPartId(),
      relations: {
        ...emptyGroup.relations,
        applianceIds: [baseAsset.id, clone.id],
        roomId: clone.relations.roomId,
      },
      airchefValues: {
        ...emptyGroup.airchefValues,
        transform: {
          ...emptyGroup.airchefValues.transform,
          position: simpleClone(
            baseAsset.airchefValues.offsetTransform?.position ?? baseAsset.airchefValues.transform.position,
          ),
          rotation: simpleClone(baseAsset.airchefValues.transform.rotation),
        },
      },
      rows: [row],
    } satisfies Group;

    baseAsset.relations.groupId = group.id;
    baseAsset.relations.rowId = row.id;
    baseAsset.relations.rowNum = 0;

    clone.relations.groupId = group.id;
    clone.relations.rowId = row.id;
    clone.relations.rowNum = 0;

    updatedGroups.push(group);
    updatedAssets.push(baseAsset);
  }

  return {
    updatedAssets,
    updatedGroups,
    newAssets,
  };
}

function updateGroupWithClone(
  original: Readonly<Appliance>,
  clone: Readonly<Appliance>,
  groups: readonly Readonly<Group>[],
): Group | null {
  const group = groups.find((g) => g.id === clone.relations.groupId);
  if (group) {
    const groupClone = simpleClone(group);
    const originalGroupIndex = groupClone.relations.applianceIds.indexOf(original.id);
    if (originalGroupIndex < 0) {
      groupClone.relations.applianceIds.push(clone.id);
    } else {
      groupClone.relations.applianceIds.splice(originalGroupIndex + 1, 0, clone.id);
    }

    const rowId = original.relations.rowId;
    const rowClone = groupClone.rows.find((r) => r.id === rowId);
    if (rowClone) {
      const isFloating = rowClone.relations.floatingApplianceIds.some((ap) => ap.applianceId === original.id);
      if (isFloating) {
        rowClone.relations.floatingApplianceIds.push({
          applianceId: clone.id,
          offset: {
            x: createMeasurement(0, 'mm', 'length'),
            y: createMeasurement(0, 'mm', 'length'),
          },
        });
      } else {
        // Appliance on floor
        const originalRowIndex = rowClone.relations.applianceIds.indexOf(original.id) ?? -1;
        if (originalRowIndex < 0) {
          rowClone.relations.applianceIds.push(clone.id);
        } else {
          rowClone.relations.applianceIds.splice(originalRowIndex + 1, 0, clone.id);
        }
      }
    }

    return groupClone;
  }
  return null;
}
